/*
 * ******************************************************************
 * Copyright (c) 2022-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { AccountStateEnum, ChargeIdEnum, CloudProviderEnum, CommerceModeEnum, DeploymentStatesEnum, DeploymentUpgradeStatusEnum, EdrDeploymentRegion, MonitorState, OAuthAppTypeEnum, ResourceAssignmentState, ResourceCategory, ResourcePoolRequestStatusType, ResourceProvider, ResourceType, RolloutType, SddcProvidersEnum, ServiceTypeEnum, VcdsDeploymentStateEnum, VcdsPersonaEnum, VcdsServiceTypeEnum, VmuaApprovalDecision } from "@api/generated/vcdr";
import { IconLabelInfo } from "@shared/services/icon/icon.model";
import { L10nService } from "@vmw/ngx-vip";
import { CspRoleEnum, VlrRoleEnum, VmcfsRoleEnum } from "@shared/services/rbac/roles.model";

export class StatesUtil {

    public static getDeploymentState(state: DeploymentStatesEnum | VcdsDeploymentStateEnum): IconLabelInfo {
        switch (state) {
            case DeploymentStatesEnum.READY:
            case VcdsDeploymentStateEnum.READY:
                return new IconLabelInfo("op.console.deployment.state.ready", "check", "success");
            case DeploymentStatesEnum.IN_PROGRESS:
            case VcdsDeploymentStateEnum.IN_PROGRESS:
                return new IconLabelInfo("op.console.deployment.state.inprogress", "sync", "progress");
            case DeploymentStatesEnum.FAILED:
            case VcdsDeploymentStateEnum.FAILED:
                return new IconLabelInfo("op.console.deployment.state.failed", "error-standard", "danger");
            case DeploymentStatesEnum.DELETED:
            case VcdsDeploymentStateEnum.DELETED:
                return new IconLabelInfo("op.console.deployment.state.deleted", "times", "danger");
            case DeploymentStatesEnum.DELETING:
            case VcdsDeploymentStateEnum.DELETING:
                return new IconLabelInfo("op.console.deployment.state.deleting", "sync", "progress");
            case DeploymentStatesEnum.DELETION_FAILED:
            case VcdsDeploymentStateEnum.DELETION_FAILED:
                return new IconLabelInfo("op.console.deployment.state.deletion.failed", "error-standard");
            case DeploymentStatesEnum.DELETION_WAITING_APPROVAL:
            case VcdsDeploymentStateEnum.DELETION_WAITING_APPROVAL:
                return new IconLabelInfo("op.console.deployment.state.deletion.waitingapproval", "clock", "progress");
            case DeploymentStatesEnum.DELETION_REQUESTED:
            case VcdsDeploymentStateEnum.DELETION_REQUESTED:
                return new IconLabelInfo("op.console.deployment.state.deletion.requested", "clock");
            case VcdsDeploymentStateEnum.DISCOVERED:
                return new IconLabelInfo("op.console.deployment.state.discovered", "search");
            default:
                console.log("Unknown deployment state: %o", state);
                return new IconLabelInfo(state);
        }
    }

    public static getLinkingRequestStatus(status: ResourcePoolRequestStatusType, approve: boolean = true): IconLabelInfo {
        switch (status) {
            case ResourcePoolRequestStatusType.PENDING_APPROVAL:
                return new IconLabelInfo("op.console.request.status.submitted", "clock");
            case ResourcePoolRequestStatusType.DONE:
                return new IconLabelInfo("op.console.request.status.accepted", "check", "success");
            case ResourcePoolRequestStatusType.DENIED_APPROVAL:
                return new IconLabelInfo("op.console.request.status.denied", "times", "danger");
            case ResourcePoolRequestStatusType.IN_PROGRESS:
                return new IconLabelInfo(
                    approve ? "op.console.request.status.approving" : "op.console.request.status.denying",
                    "sync", "progress");
            case ResourcePoolRequestStatusType.FAILED:
                return new IconLabelInfo("op.console.request.status.failed", "error-standard", "danger");
            default:
                console.log("Unknown request status: %o", status);
                return new IconLabelInfo(status);
        }
    }

    public static getQuorumApprovalDecisionStatus(status: VmuaApprovalDecision.DecisionEnum): IconLabelInfo {
        switch (status) {
            case VmuaApprovalDecision.DecisionEnum.APPROVE:
                return new IconLabelInfo("op.console.request.status.accepted", "check", "success");
            case VmuaApprovalDecision.DecisionEnum.DENY:
                return new IconLabelInfo("op.console.request.status.denied", "times", "danger");
            default:
                console.log("Unknown approval decision status: %o", status);
                return new IconLabelInfo(status);
        }
    }

    public static serviceToString(serviceType: ServiceTypeEnum | string, long: boolean = true): string {
        let resource: string;
        switch (serviceType) {
            case ServiceTypeEnum.VCDR:
                resource = long ? "console.service.vlr.long" : "console.service.vlr";
                break;
            case ServiceTypeEnum.VMCFS:
                resource = long ? "console.service.vmcfs.long" : "console.service.vmcfs";
                break;
            default:
                resource = null;
                break;
        }
        return resource;
    }

    public static serviceTypeToString(vcdsServiceType: VcdsServiceTypeEnum, long: boolean = true): string {
        let resource: string;
        switch (vcdsServiceType) {
            case VcdsServiceTypeEnum.VMCFS:
                resource = long ? "console.service.type.vmcfs.long" : "console.service.type.vmcfs";
                break;
            case VcdsServiceTypeEnum.VCDR:
                resource = long ? "console.service.type.vlcr.long" : "console.service.type.vlcr";
                break;
            case VcdsServiceTypeEnum.SRM:
                resource = long ? "console.service.type.vlsr.long" : "console.service.type.vlsr";
                break;
            case VcdsServiceTypeEnum.VCDS:
                resource = long ? "console.service.type.vcds.long" : "console.service.type.vcds";
                break;
            default:
                resource = "general.UNDEFINED_STRING";
                break;
        }
        return resource;
    }

    public static getUpgradeStatus(upgradeStatus: DeploymentUpgradeStatusEnum): string {
        switch (upgradeStatus) {
            case DeploymentUpgradeStatusEnum.ALLOWED:
                return "op.console.datagrid.instances.upgrade.allowed";
            case DeploymentUpgradeStatusEnum.NOT_ALLOWED:
                return "op.console.datagrid.instances.upgrade.notAllowed";
            case DeploymentUpgradeStatusEnum.ON_HOLD:
                return "op.console.datagrid.instances.upgrade.onHold";
        }
        return "general.UNDEFINED_STRING";
    }

    public static commerceModeToString(commerceMode: CommerceModeEnum): string {
        switch (commerceMode) {
            case CommerceModeEnum.LEGACY_EVAL:
                return "deployment.commerce.mode.legacyEval";
            case CommerceModeEnum.LEGACY_STANDARD:
                return "deployment.commerce.mode.legacyStandard";
            case CommerceModeEnum.LEGACY_ENTERPRISE:
                return "deployment.commerce.mode.legacyEnterprise";
            case CommerceModeEnum.LEGACY_CLOUD:
                return "deployment.commerce.mode.legacyCloud";
            case CommerceModeEnum.LEGACY_TERM:
                return "deployment.commerce.mode.legacyTerm";
            case CommerceModeEnum.LEGACY_UNKNOWN:
                return "deployment.commerce.mode.legacyUnknown";
            case CommerceModeEnum.CLOUD_TRIAL:
                return "deployment.commerce.mode.cloudTrial";
            case CommerceModeEnum.CLOUD_TRIAL_DEGRADED:
                return "deployment.commerce.mode.cloudTrialDegraded";
            case CommerceModeEnum.CLOUD_TRIAL_EXPIRED:
                return "deployment.commerce.mode.cloudTrialExpired";
            case CommerceModeEnum.CLOUD_SUB:
                return "deployment.commerce.mode.cloudSub";
            case CommerceModeEnum.CLOUD_SUB_DEGRADED:
                return "deployment.commerce.mode.cloudSubDegraded";
            case CommerceModeEnum.CLOUD_SUB_SUSPENDED:
                return "deployment.commerce.mode.cloudSubSuspended";
            case CommerceModeEnum.DISCONNECTED_SUB:
                return "deployment.commerce.mode.disconnectedSub";
            case CommerceModeEnum.DISCONNECTED_SUB_DEGRADED:
                return "deployment.commerce.mode.disconnectedSubDegraded";
            case CommerceModeEnum.DISCONNECTED_SUB_SUSPENDED:
                return "deployment.commerce.mode.disconnectedSubSuspended";
            case CommerceModeEnum.DISCONNECTED_SUB_WARNING:
                return "deployment.commerce.mode.disconnectedSubWarning";
            case CommerceModeEnum.DISCONNECTED_SUB_GRACE:
                return "deployment.commerce.mode.disconnectedSubGrace";
            case CommerceModeEnum.DISCONNECTED_SUB_TERMINATED:
                return "deployment.commerce.mode.disconnectedSubTerminated";
            default:
                return "general.UNDEFINED_STRING";
        }
    }

    public static getMonitorStateLabel(monitorState: MonitorState): string {
        switch (monitorState) {
            case MonitorState.ACTIVE:
                return "deployment.monitor.state.active";
            case MonitorState.PAUSED:
                return "deployment.monitor.state.paused";
            case MonitorState.CREATION_PENDING:
                return "deployment.monitor.state.pending.creation";
            case MonitorState.DELETION_PENDING:
                return "deployment.monitor.state.pending.deletion";
            case MonitorState.DELETED:
                return "deployment.monitor.state.deleted";
            default:
                return "general.UNDEFINED_STRING";
        }
    }

    public static getCapacityResourceString(chargeId: any): string {
        switch (chargeId) {
            case ChargeIdEnum.DAT_VM:
                return "subscriptions.quantity.inVMs.cloud";
            case ChargeIdEnum.DAT_SVM:
                return "subscriptions.quantity.inVMs.onprem";
            case ChargeIdEnum.DAT_RWR:
                return "subscriptions.quantity.inRwrVMs";
            case ChargeIdEnum.DAT_TIB:
            case ChargeIdEnum.CFS_GIB:
            default:
                return "subscriptions.quantity.inTiB";
        }
    }

    public static getCloudProviderLabel(cloudProvider: CloudProviderEnum): string {
        switch (cloudProvider) {
            case CloudProviderEnum.AWS:
                return "op.console.resource.provider.aws";
            case CloudProviderEnum.GCP:
                return "op.console.resource.provider.gcp";
            case CloudProviderEnum.ON_PREM:
                return "op.console.cloud.provider.onprem";
            default:
                return "general.UNDEFINED_STRING";
        }
    }

    public static getProviderLabel(provider: ResourceProvider): string {
        switch (provider) {
            case ResourceProvider.AWS:
                return "op.console.resource.provider.aws";
            case ResourceProvider.GCP:
                return "op.console.resource.provider.gcp";
            case ResourceProvider.CWS:
                return "op.console.resource.provider.cws";
            case ResourceProvider.GCVE:
                return "op.console.resource.provider.gcve";
            case ResourceProvider.VMCA:
                return "op.console.resource.provider.vmca";
            case ResourceProvider.CBC:
                return "op.console.resource.provider.cbc";
            case ResourceProvider.EVS:
                return "op.console.resource.provider.evs";
            default:
                console.log("Unknown resource provider: %o", provider);
                return "general.UNDEFINED_STRING";
        }
    }

    public static getProviderFullLabel(provider: ResourceProvider): string {
        switch (provider) {
            case ResourceProvider.AWS:
                return "op.console.org.summary.organization.account.aws";
            case ResourceProvider.GCP:
                return "op.console.org.summary.organization.account.gcp";
            case ResourceProvider.GCVE:
                return "op.console.org.summary.organization.account.gcve";
            default:
                return null;
        }
    }

    public static getCategoryLabel(category: ResourceCategory): string {
        switch (category) {
            case ResourceCategory.INTERNAL:
                return "op.console.resource.category.internal";
            case ResourceCategory.EXTERNAL:
                return "op.console.resource.category.external";
            case ResourceCategory.POC:
                return "op.console.resource.category.poc";
            case ResourceCategory.ENGINEERING:
                return "op.console.resource.category.engineering";
            case ResourceCategory.ENGINEERING_SM:
                return "op.console.resource.category.engineering.sm";
            case ResourceCategory.ENGINEERING_QA:
                return "op.console.resource.category.engineering.qa";
            case ResourceCategory.ENGINEERING_BA:
                return "op.console.resource.category.engineering.ba";
            case ResourceCategory.ENGINEERING_FS:
                return "op.console.resource.category.engineering.fs";
            case ResourceCategory.ENGINEERING_SP:
                return "op.console.resource.category.engineering.sp";
            default:
                console.log("Unknown resource category: %o", category);
                return "general.UNDEFINED_STRING";
        }
    }

    public static getResourceStateLabel(state: AccountStateEnum): string {
        switch (state) {
            case AccountStateEnum.ONBOARDED:
                return "op.console.resource.state.onboarded";
            case AccountStateEnum.READY:
                return "op.console.resource.state.ready";
            case AccountStateEnum.ASSIGNED:
                return "op.console.resource.state.assigned";
            case AccountStateEnum.FIXING:
                return "op.console.resource.state.fixing";
            case AccountStateEnum.TOMBSTONED:
                return "op.console.resource.state.tombstoned";
            case AccountStateEnum.ABANDONED:
                return "op.console.resource.state.abandoned";
            default:
                console.log("Unknown resource state: %o", state);
                return "general.UNDEFINED_STRING";
        }
    }

    public static getResourceTypeLabel(state: ResourceType): string {
        switch (state) {
            case ResourceType.DYNAMIC:
                return "op.console.resource.type.dynamic";
            case ResourceType.STATIC:
                return "op.console.resource.type.static";
            default:
                console.log("Unknown resource type: %o", state);
                return "general.UNDEFINED_STRING";
        }
    }

    public static getResourceAssignmentStateLabel(state: ResourceAssignmentState): string {
        switch (state) {
            case ResourceAssignmentState.ASSIGNED:
                return "op.console.resource.assignment.state.assigned";
            case ResourceAssignmentState.ASSIGNING:
                return "op.console.resource.assignment.state.assigning";
            case ResourceAssignmentState.UNASSIGNED:
                return "op.console.resource.assignment.state.unassigned";
            case ResourceAssignmentState.UNASSIGNING:
                return "op.console.resource.assignment.state.unassigning";
            default:
                console.log("Unknown resource assignment state: %o", state);
                return "general.UNDEFINED_STRING";
        }
    }

    public static getEdrStatusLabel(state: EdrDeploymentRegion.StatusEnum): string {
        switch (state) {
            case EdrDeploymentRegion.StatusEnum.ACTIVATED:
                return "op.console.edr.status.activated";
            case EdrDeploymentRegion.StatusEnum.NOT_USED:
                return "op.console.edr.status.notused";
            default:
                console.log("Unknown resource state: %o", state);
                return "general.UNDEFINED_STRING";
        }
    }

    public static getEngineTypeLabel(engine: SddcProvidersEnum): string {
        switch (engine) {
            case SddcProvidersEnum.EVS:
                return "console.cloudprovider.evs";
            case SddcProvidersEnum.VMC:
                return "console.cloudprovider.aws";
            case SddcProvidersEnum.GCVE:
                return "console.cloudprovider.gcp";
            default:
                console.log("Unknown engine: %o", engine);
                return "general.UNDEFINED_STRING";
        }
    }

    public static getPersonaLabel(persona: VcdsPersonaEnum): string {
        switch (persona) {
            case VcdsPersonaEnum.VLSR:
                return "console.persona.vlsr";
            case VcdsPersonaEnum.VLSR_MTDR:
                return "console.persona.mtdr";
            case VcdsPersonaEnum.VLCR_CLOUD:
                return "console.persona.cloud";
            case VcdsPersonaEnum.VLCR_ONPREM:
                return "console.persona.onprem";
            case VcdsPersonaEnum.VMCFS:
                return "console.persona.vmcfs";
            default:
                return "general.UNDEFINED_STRING";
        }
    }

    public static getOAuthAppTypeLabel(oAuthAppType: OAuthAppTypeEnum): string {
        switch (oAuthAppType) {
            case OAuthAppTypeEnum.NATIVE:
                return "op.console.oauthapp.type.native";
            case OAuthAppTypeEnum.SRM_AGENT:
                return "op.console.oauthapp.type.srmagent";
            case OAuthAppTypeEnum.ASUP:
                return "op.console.oauthapp.type.asup";
            case OAuthAppTypeEnum.VCF:
                return "op.console.oauthapp.type.vcf";
            case OAuthAppTypeEnum.VMC:
                return "op.console.oauthapp.type.vmc";
            case OAuthAppTypeEnum.VMC_POOL:
                return "op.console.oauthapp.type.vmc.pool";
            case OAuthAppTypeEnum.DRC:
                return "op.console.oauthapp.type.drc";
            default:
                return "general.UNDEFINED_STRING";
        }
    }

    public static getVlcrRoleLabel(role: VlrRoleEnum): string {
        switch (role) {
            case VlrRoleEnum.VLCR_ADMIN:
                return "vlcr.role.vlcr.admin";
            case VlrRoleEnum.VLCR_DR_ADMIN:
                return "vlcr.role.vlcr.dr.admin";
            case VlrRoleEnum.VLCR_DR_SDDC_ADMIN:
                return "vlcr.role.vlcr.dr.sddc.admin";
            case VlrRoleEnum.VLCR_DR_TESTER:
                return "vlcr.role.vlcr.dr.tester";
            case VlrRoleEnum.VLCR_AUDITOR:
                return "vlcr.role.vlcr.auditor";
            case VlrRoleEnum.VLCR_BACKUP_ADMIN:
                return "vlcr.role.vlcr.backup.admin";
            case VlrRoleEnum.CONSOLE_ADMIN:
                return "vlcr.role.console.admin";
            case VlrRoleEnum.CONSOLE_ACTIVATE_DEPLOYMENT:
                return "vlcr.role.console.activate.deployment";
            case VlrRoleEnum.CONSOLE_DEACTIVATE_DEPLOYMENT:
                return "vlcr.role.console.deactivate.deployment";
            case VlrRoleEnum.CONSOLE_SUBSCRIPTION_ADMIN:
                return "vlcr.role.console.subscription.admin";
            case VlrRoleEnum.CONSOLE_SUBSCRIPTION_AUDITOR:
                return "vlcr.role.console.subscription.auditor";
            default:
                return "general.UNDEFINED_STRING";
        }
    }

    public static getVmcfsRoleLabel(role: VmcfsRoleEnum): string {
        switch (role) {
            case VmcfsRoleEnum.STORAGE_ADMIN:
                return "vmcfs.role.storage.admin";
            case VmcfsRoleEnum.STORAGE_AUDITOR:
                return "vmcfs.role.storage.auditor";
            case VmcfsRoleEnum.FILESYSTEM_ADMIN:
                return "vmcfs.role.filesystem.admin";
            case VmcfsRoleEnum.FILESYSTEM_ADMIN_RESTRICTED:
                return "vmcfs.role.filesystem.admin.restricted";
            case VmcfsRoleEnum.CONSOLE_ADMIN:
                return "vmcfs.role.console.admin";
            case VmcfsRoleEnum.CONSOLE_ACTIVATE_DEPLOYMENT:
                return "vmcfs.role.console.activate.deployment";
            case VmcfsRoleEnum.CONSOLE_DEACTIVATE_DEPLOYMENT:
                return "vmcfs.role.console.deactivate.deployment";
            case VmcfsRoleEnum.CONSOLE_SUBSCRIPTION_ADMIN:
                return "vmcfs.role.console.subscription.admin";
            case VmcfsRoleEnum.CONSOLE_STORAGE_ADMIN:
                return "vmcfs.role.console.storage.admin";
            default:
                return "general.UNDEFINED_STRING";
        }
    }

    public static getOrgRoleLabel(role: CspRoleEnum): string {
        if (role === CspRoleEnum.ORG_MEMBER) {
            return "op.console.email.select.recipient.org.member";
        } else if (role === CspRoleEnum.ORG_OWNER) {
            return "op.console.email.select.recipient.org.owner";
        } else {
            return "general.UNDEFINED_STRING";
        }
    }

    public static getRolloutTypeLabel(type: RolloutType): string {
        switch (type) {
            case RolloutType.IRR_UPGRADE:
                return "op.console.setup.rolloutPlans.action.upgrade";
            case RolloutType.OAUTH_APP_ROTATE:
                return "op.console.setup.rolloutPlans.action.oAuthApp.rotation";
            case RolloutType.IRR_GW_CERT_UPDATE:
                return "op.console.setup.rolloutPlans.action.irr.cert.rotation";
            default:
                return type;
        }
    }

    public static getFlagLabel(flag: boolean, required: boolean = true): string {
        if (flag === true) {
            return "general.yes";
        } else if (flag === false) {
            return "general.no";
        }
        return required ? "general.no" : "general.UNDEFINED_STRING";
    }

    public static getLabel(value: any, l10nService: L10nService): string {
        if (!value) {
            return l10nService.getMessage("general.UNDEFINED_STRING");
        }
        return value;
    }

    public static getEnumValueToString(value: string, resourcePrefix: string, l10nService: L10nService): string {
        if (!value) {
            return l10nService.getMessage("general.UNDEFINED_STRING");
        }

        const resourceId = resourcePrefix + value;
        if (l10nService.isExistKey(resourceId)) {
            return l10nService.getMessage(resourceId);
        }

        let result = value.replace(/-/g, " ");
        result = result.charAt(0).toUpperCase() + result.slice(1).toLowerCase();
        return result;
    }
}
